import { useEffect, useRef, useState } from "react";
import { CheckCheck, Mail, MessageCircle, Send, X } from "lucide-react";
import {
  loadChatRouteScript,
  receiveChatMessages,
  sendChatMessage,
  subscribeToChatMessages,
  suppressInjectedChatUi,
} from "../lib/chatBridge";
import {
  OLIVEUP_HELLO_EMAIL,
  OLIVEUP_MAILTO,
  isOliveUpLiveChatHours,
} from "../lib/contact";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";

const STORAGE_KEY = "oliveup_chat_visitor_v1";

const TOPIC_OPTIONS = [
  { value: "funding", label: "Business funding" },
  { value: "processing", label: "No-fee processing (Profit Press)" },
  { value: "other", label: "Something else" },
];

function getStoredVisitor() {
  if (typeof sessionStorage === "undefined") return null;
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (
      parsed &&
      typeof parsed.name === "string" &&
      parsed.name.trim().length >= 2 &&
      typeof parsed.email === "string" &&
      parsed.email.includes("@")
    ) {
      return {
        name: String(parsed.name).trim(),
        email: String(parsed.email).trim(),
        phone: typeof parsed.phone === "string" ? parsed.phone.trim() : "",
        topic: typeof parsed.topic === "string" ? parsed.topic : "funding",
      };
    }
  } catch {
    /* ignore */
  }
  return null;
}

function topicLabel(topic) {
  return TOPIC_OPTIONS.find((t) => t.value === topic)?.label || "your question";
}

function isVisitorFrom(m) {
  const f = String(m?.from ?? "").toLowerCase();
  return f === "visitor" || f === "user" || f === "customer";
}

const AFTER_HOURS_SUPPORT_TEXT = `We're outside live chat hours (Mon–Fri, 8a–8p Eastern). Your messages still land here—our team will pick them up as soon as we're back online. For anything urgent, email ${OLIVEUP_HELLO_EMAIL}.`;

function appendAfterHoursIfNeeded(list) {
  if (isOliveUpLiveChatHours()) return list;
  const notice = {
    id: `after-hours-${Date.now()}`,
    from: "support",
    text: AFTER_HOURS_SUPPORT_TEXT,
  };
  return [...list, notice];
}

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(() => (getStoredVisitor() ? "chat" : "gate"));
  const [visitorMeta, setVisitorMeta] = useState(() => getStoredVisitor());
  const [gateForm, setGateForm] = useState({
    name: "",
    email: "",
    phone: "",
    topic: "funding",
  });
  const [gateErrors, setGateErrors] = useState({});
  const [messages, setMessages] = useState(() => {
    const v = getStoredVisitor();
    if (!v) return [];
    const first = v.name.trim().split(/\s+/)[0] || "there";
    const welcome = {
      id: "reopen-welcome",
      from: "support",
      text: `Welcome back, ${first}. What can we help with today?`,
    };
    return appendAfterHoursIfNeeded([welcome]);
  });
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const messagesRef = useRef(null);
  const inputRef = useRef(null);
  const visitorMetaRef = useRef(visitorMeta);
  visitorMetaRef.current = visitorMeta;

  useEffect(() => {
    const openChat = () => setOpen(true);
    window.oliveUpChatOpen = openChat;
    window.addEventListener("oliveup:open-chat", openChat);

    return () => {
      window.removeEventListener("oliveup:open-chat", openChat);
      if (window.oliveUpChatOpen === openChat) {
        delete window.oliveUpChatOpen;
      }
    };
  }, []);

  useEffect(() => {
    loadChatRouteScript().catch(() => {});
    const receiveMessage = (message) => {
      setMessages((current) => {
        if (!message?.id || current.some((m) => m.id === message.id)) return current;
        return [...current, message];
      });
    };
    const unsubscribe = subscribeToChatMessages(receiveMessage);

    receiveChatMessages()
      .then((incoming) => incoming.forEach(receiveMessage))
      .catch(() => {});

    return unsubscribe;
  }, []);

  useEffect(() => {
    if (!open) return;
    suppressInjectedChatUi();
    window.requestAnimationFrame(() => {
      if (step === "chat") inputRef.current?.focus();
    });
  }, [open, step]);

  useEffect(() => {
    if (!messagesRef.current) return;
    messagesRef.current.scrollTop = messagesRef.current.scrollHeight;
  }, [messages, open, step]);

  const addMessage = (message) => {
    setMessages((current) => {
      if (!message?.id || current.some((m) => m.id === message.id)) return current;
      return [...current, message];
    });
  };

  const validateGate = () => {
    const err = {};
    if (gateForm.name.trim().length < 2) err.name = "Please enter your name.";
    const email = gateForm.email.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) err.email = "Enter a valid email.";
    if (!gateForm.topic) err.topic = "Pick a topic.";
    setGateErrors(err);
    return Object.keys(err).length === 0;
  };

  const submitGate = (event) => {
    event.preventDefault();
    if (!validateGate()) return;

    const meta = {
      name: gateForm.name.trim(),
      email: gateForm.email.trim(),
      phone: gateForm.phone.trim(),
      topic: gateForm.topic,
    };
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(meta));
    } catch {
      /* ignore quota / private mode */
    }
    setVisitorMeta(meta);

    const first = meta.name.split(/\s+/)[0] || "there";
    const welcome = {
      id: `welcome-${Date.now()}`,
      from: "support",
      text: `Thanks, ${first}. You're interested in ${topicLabel(meta.topic)}. Tell us about your business, rough monthly revenue, and when you need funds.`,
    };
    const next = appendAfterHoursIfNeeded([welcome]);
    setMessages(next);
    setStep("chat");
  };

  const sendMessage = async (event) => {
    event.preventDefault();
    const text = draft.trim();
    const meta = visitorMetaRef.current;
    if (!text || !meta) {
      return;
    }

    const msgId = `visitor-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
    setMessages((current) => {
      if (current.some((m) => m.id === msgId)) return current;
      return [
        ...current,
        {
          id: msgId,
          from: "visitor",
          text,
          deliveryStatus: "pending",
        },
      ];
    });
    setDraft("");
    setSending(true);

    try {
      const incoming = await sendChatMessage(text, meta);
      setMessages((cur) =>
        cur.map((m) =>
          m.id === msgId && isVisitorFrom(m)
            ? { ...m, deliveryStatus: "delivered" }
            : m,
        ),
      );
      incoming.forEach(addMessage);
    } catch {
      setMessages((cur) =>
        cur.map((m) =>
          m.id === msgId && isVisitorFrom(m)
            ? { ...m, deliveryStatus: "failed" }
            : m,
        ),
      );
      addMessage({
        id: `chat-error-${Date.now()}`,
        from: "support",
        text: `The chat service is not reachable right now. Email us at ${OLIVEUP_HELLO_EMAIL} and we will reply quickly.`,
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid="chat-launcher"
        aria-label="Open live chat with OliveUp"
        className="fixed bottom-5 right-5 z-[70] inline-flex h-14 max-w-[calc(100vw-2.5rem)] items-center justify-center gap-2 rounded-full bg-primary px-4 text-primary-foreground shadow-[0_14px_30px_rgba(44,53,29,0.22)] transition-transform hover:-translate-y-0.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background sm:min-w-[9.5rem] sm:px-5"
      >
        <MessageCircle className="h-6 w-6 shrink-0" />
        <span className="hidden text-sm font-semibold sm:inline">Live chat</span>
      </button>

      {open && (
        <div
          data-testid="chat-panel"
          role="dialog"
          aria-modal="false"
          aria-labelledby="chat-title"
          className="oliveup-live-chat-panel fixed inset-x-3 bottom-3 z-[80] mx-auto flex h-[min(32rem,calc(100vh-5rem))] max-w-[min(100%,26rem)] flex-col overflow-hidden rounded-2xl border border-border bg-card text-card-foreground shadow-[0_24px_70px_rgba(44,53,29,0.24)] sm:inset-x-auto sm:right-5 sm:mx-0 sm:max-w-[28rem]"
        >
          <div className="flex shrink-0 items-center justify-between gap-4 bg-primary px-4 py-3 text-primary-foreground">
            <div className="min-w-0">
              <h2 id="chat-title" className="font-display text-base font-bold">
                OliveUp live chat
              </h2>
              <p className="text-xs text-primary-foreground/85">
                Mon–Fri, 8a–8p ET • We route you to the right specialist
              </p>
            </div>
            <button
              type="button"
              onClick={() => setOpen(false)}
              aria-label="Close chat"
              className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-primary-foreground/90 transition-colors hover:bg-white/12 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/70"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {step === "gate" ? (
            <form
              onSubmit={submitGate}
              className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto border-t border-border bg-[#F9F8F4] p-4"
              data-testid="chat-gate-form"
            >
              <p className="text-sm text-muted-foreground leading-relaxed">
                A few details help us respond faster and route your conversation to funding,
                processing, or general support.
              </p>
              <div className="space-y-1.5">
                <Label htmlFor="chat-gate-name">Name</Label>
                <Input
                  id="chat-gate-name"
                  data-testid="chat-gate-name"
                  value={gateForm.name}
                  onChange={(e) =>
                    setGateForm((f) => ({ ...f, name: e.target.value }))
                  }
                  autoComplete="name"
                  className="h-10 bg-background"
                  placeholder="Your name"
                />
                {gateErrors.name && (
                  <p className="text-xs text-destructive">{gateErrors.name}</p>
                )}
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="chat-gate-email">Work email</Label>
                <Input
                  id="chat-gate-email"
                  data-testid="chat-gate-email"
                  type="email"
                  inputMode="email"
                  value={gateForm.email}
                  onChange={(e) =>
                    setGateForm((f) => ({ ...f, email: e.target.value }))
                  }
                  autoComplete="email"
                  className="h-10 bg-background"
                  placeholder="you@business.com"
                />
                {gateErrors.email && (
                  <p className="text-xs text-destructive">{gateErrors.email}</p>
                )}
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="chat-gate-phone">Phone (optional)</Label>
                <Input
                  id="chat-gate-phone"
                  data-testid="chat-gate-phone"
                  type="tel"
                  inputMode="tel"
                  value={gateForm.phone}
                  onChange={(e) =>
                    setGateForm((f) => ({ ...f, phone: e.target.value }))
                  }
                  autoComplete="tel"
                  className="h-10 bg-background"
                  placeholder="Best number for a quick call-back"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="chat-gate-topic">What brings you in?</Label>
                <select
                  id="chat-gate-topic"
                  data-testid="chat-gate-topic"
                  value={gateForm.topic}
                  onChange={(e) =>
                    setGateForm((f) => ({ ...f, topic: e.target.value }))
                  }
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                >
                  {TOPIC_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
                {gateErrors.topic && (
                  <p className="text-xs text-destructive">{gateErrors.topic}</p>
                )}
              </div>
              <Button
                type="submit"
                data-testid="chat-gate-continue"
                className="mt-1 w-full rounded-full bg-accent font-semibold text-accent-foreground hover:bg-[#D1664A]"
              >
                Continue to chat
              </Button>
              <a
                href={OLIVEUP_MAILTO}
                data-testid="chat-email"
                className="inline-flex items-center gap-1.5 text-xs font-semibold text-primary hover:text-[#D1664A]"
              >
                <Mail className="h-3.5 w-3.5" />
                Prefer email? {OLIVEUP_HELLO_EMAIL}
              </a>
            </form>
          ) : (
            <div className="flex min-h-0 flex-1 flex-col border-t border-border">
              <div
                ref={messagesRef}
                data-testid="chat-messages"
                className="oliveup-visitor-feed flex-1 basis-0 min-h-[10rem] space-y-3 overflow-y-auto bg-[#F9F8F4] px-4 py-4"
                aria-live="polite"
              >
                {messages.map((message) => {
                  const mine = isVisitorFrom(message);
                  return (
                  <div
                    key={message.id}
                    className={`flex ${mine ? "justify-end" : "justify-start"}`}
                  >
                    <div
                      className={`flex max-w-[88%] flex-col gap-0.5 ${mine ? "items-end" : "items-start"}`}
                    >
                      <div
                        className={`rounded-xl px-3 py-2 text-sm leading-relaxed ${
                          mine
                            ? "bg-accent text-accent-foreground"
                            : "border border-border bg-card text-card-foreground shadow-sm"
                        }`}
                      >
                        {message.text ?? ""}
                      </div>
                      {mine && message.deliveryStatus && (
                        <div
                          className="flex items-center gap-0.5 text-red-600"
                          data-testid={`chat-receipt-${message.id}`}
                          aria-live="polite"
                        >
                          {message.deliveryStatus === "pending" && (
                            <span className="text-[10px] font-medium">Sending…</span>
                          )}
                          {message.deliveryStatus === "delivered" && (
                            <>
                              <CheckCheck className="h-3.5 w-3.5 shrink-0" strokeWidth={2.4} aria-hidden />
                              <span className="text-[10px] font-semibold uppercase tracking-wide">
                                Delivered
                              </span>
                            </>
                          )}
                          {message.deliveryStatus === "failed" && (
                            <span className="text-[10px] font-medium text-destructive">
                              Not delivered
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                );
                })}
              </div>

              <div className="shrink-0 border-t border-border bg-card px-3 py-2 text-[11px] text-muted-foreground">
                Logged in as{" "}
                <span className="font-medium text-foreground">{visitorMeta?.name}</span>
                {" · "}
                {topicLabel(visitorMeta?.topic)}
              </div>

              <form
                onSubmit={sendMessage}
                className="shrink-0 border-t border-border bg-card p-3"
                data-testid="chat-form"
              >
                <div className="flex items-center gap-2">
                  <input
                    ref={inputRef}
                    value={draft}
                    onChange={(event) => setDraft(event.target.value)}
                    data-testid="chat-input"
                    type="text"
                    placeholder="Type your message"
                    className="h-11 min-w-0 flex-1 rounded-lg border border-input bg-background px-3 text-sm outline-none transition-colors placeholder:text-muted-foreground focus:border-primary focus:ring-2 focus:ring-primary/20"
                  />
                  <button
                    type="submit"
                    data-testid="chat-send"
                    aria-label="Send message"
                    disabled={!draft.trim() || sending}
                    className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-accent text-accent-foreground transition-colors hover:bg-[#D1664A] disabled:cursor-not-allowed disabled:opacity-45"
                  >
                    <Send className="h-4 w-4" />
                  </button>
                </div>
                <a
                  href={OLIVEUP_MAILTO}
                  className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold text-primary hover:text-[#D1664A]"
                >
                  <Mail className="h-3.5 w-3.5" />
                  {OLIVEUP_HELLO_EMAIL}
                </a>
              </form>
            </div>
          )}
        </div>
      )}
    </>
  );
}
